import { useState } from 'react';
import { Download, Search } from 'lucide-react';

export interface ReplayDatasetRow {
  model: string;
  runtime: string;
  problem: string;
  tokens_in: number;
  tokens_out: number;
  snippet: string;
}

export interface ReplayDatasetSnapshot {
  kind: 'dataset';
  title: string;
  description: string;
  row_count: number;
  rows: ReplayDatasetRow[];
}

interface Props {
  replayId: string;
  snapshot: ReplayDatasetSnapshot;
}

function formatCount(value: number): string {
  return value.toLocaleString();
}

function downloadFile(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function escapeCsvCell(value: string | number): string {
  const stringValue = String(value);
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

export function isReplayDatasetSnapshot(
  snapshot: unknown,
): snapshot is ReplayDatasetSnapshot {
  if (!snapshot || typeof snapshot !== 'object') return false;
  const candidate = snapshot as Partial<ReplayDatasetSnapshot>;
  return (
    candidate.kind === 'dataset' &&
    typeof candidate.title === 'string' &&
    typeof candidate.description === 'string' &&
    typeof candidate.row_count === 'number' &&
    Array.isArray(candidate.rows)
  );
}

export default function ReplayDataset({ replayId, snapshot }: Props) {
  const [filter, setFilter] = useState('');
  const normalizedFilter = filter.trim().toLowerCase();
  const filteredRows = normalizedFilter
    ? snapshot.rows.filter((row) => {
        const model = row.model.toLowerCase();
        const problem = row.problem.toLowerCase();
        return model.includes(normalizedFilter) || problem.includes(normalizedFilter);
      })
    : snapshot.rows;

  const baseFilename = replayId || 'dataset';

  function handleDownloadJson() {
    downloadFile(
      `${baseFilename}.json`,
      JSON.stringify(snapshot.rows, null, 2),
      'application/json',
    );
  }

  function handleDownloadCsv() {
    const header = ['model', 'runtime', 'problem', 'tokens_in', 'tokens_out', 'snippet'];
    const lines = [
      header.join(','),
      ...snapshot.rows.map((row) =>
        [
          row.model,
          row.runtime,
          row.problem,
          row.tokens_in,
          row.tokens_out,
          row.snippet,
        ]
          .map(escapeCsvCell)
          .join(','),
      ),
    ];
    downloadFile(`${baseFilename}.csv`, lines.join('\n'), 'text/csv;charset=utf-8');
  }

  return (
    <section className="rounded-xl border border-[#2a2a3a] bg-[#111119] overflow-hidden">
      <div className="border-b border-[#2a2a3a] px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <div>
              <h1 className="text-lg font-semibold text-white">{snapshot.title}</h1>
              <p className="mt-1 text-sm leading-relaxed text-[#8888a0]">
                {snapshot.description}
              </p>
            </div>
            <p className="text-sm text-[#aaaab8]">
              <span className="font-medium text-white">{formatCount(snapshot.row_count)}</span> runs
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleDownloadJson}
              className="inline-flex items-center gap-2 rounded-md border border-[#2a2a3a] bg-[#16161e] px-3 py-2 text-sm text-[#aaaab8] hover:border-[#00FFB2]/40 hover:text-white transition-colors"
            >
              <Download className="h-4 w-4" />
              Download JSON
            </button>
            <button
              onClick={handleDownloadCsv}
              className="inline-flex items-center gap-2 rounded-md border border-[#2a2a3a] bg-[#16161e] px-3 py-2 text-sm text-[#aaaab8] hover:border-[#00FFB2]/40 hover:text-white transition-colors"
            >
              <Download className="h-4 w-4" />
              Download CSV
            </button>
          </div>
        </div>
        <div className="mt-4 relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5a5a6e]" />
          <input
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Filter by model or problem"
            className="w-full rounded-md border border-[#2a2a3a] bg-[#0f0f16] py-2 pl-9 pr-3 text-sm text-white outline-none placeholder:text-[#5a5a6e] focus:border-[#00FFB2]/50"
          />
        </div>
      </div>

      <div className="max-h-[70vh] overflow-y-auto">
        <table className="min-w-full border-collapse">
          <thead className="sticky top-0 z-[1] bg-[#0f0f16]">
            <tr className="border-b border-[#2a2a3a] text-left text-xs uppercase tracking-wide text-[#8888a0]">
              <th className="px-4 py-3 font-medium sm:px-5">Model</th>
              <th className="px-4 py-3 font-medium sm:px-5">Problem</th>
              <th className="px-4 py-3 font-medium sm:px-5">Tokens in</th>
              <th className="px-4 py-3 font-medium sm:px-5">Tokens out</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row, index) => (
              <tr
                key={`${row.model}-${row.runtime}-${row.problem}-${index}`}
                className="group border-b border-[#1b1b26] align-top hover:bg-[#14141d]"
                title={row.snippet}
              >
                <td className="px-4 py-3 text-sm text-white sm:px-5">
                  <div className="font-medium">{row.model}</div>
                  <div className="mt-1 text-xs text-[#5a5a6e]">{row.runtime}</div>
                </td>
                <td className="px-4 py-3 text-sm text-white sm:px-5">
                  <div>{row.problem}</div>
                  <div className="mt-1 max-w-xl truncate text-xs text-[#8888a0] group-hover:text-[#aaaab8]">
                    {row.snippet}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-[#aaaab8] tabular-nums sm:px-5">
                  {formatCount(row.tokens_in)}
                </td>
                <td className="px-4 py-3 text-sm text-[#aaaab8] tabular-nums sm:px-5">
                  {formatCount(row.tokens_out)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredRows.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-[#8888a0]">
            No rows match this filter.
          </div>
        )}
      </div>
    </section>
  );
}
